import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

interface AccessTokenResponse {
  tokenType: string;
  accessToken: string;
  expiresIn: number;
  refreshToken: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private http = inject(HttpClient);
  private readonly baseUrl = environment.apiUrl;
  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly TOKEN_EXPIRY_KEY = 'token_expiry';

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.hasValidToken());

  // Track ongoing refresh request to prevent concurrent calls
  private refreshPromise: Promise<AccessTokenResponse> | null = null;

  async login(email: string, password: string): Promise<AccessTokenResponse> {
    try {
      const response = await firstValueFrom(
        this.http.post<AccessTokenResponse>(`${this.baseUrl}/api/auth/login?useCookies=false`, {
          email,
          password,
        }),
      );

      this.storeTokens(response);
      this.isAuthenticatedSubject.next(true);
      return response;
    } catch (error) {
      this.handleAuthError(error);
      throw error;
    }
  }

  logout() {
    this.clearTokens();
    this.isAuthenticatedSubject.next(false);
  }

  async getToken(): Promise<string | null> {
    const token = localStorage.getItem(this.ACCESS_TOKEN_KEY);

    if (!token) {
      return null;
    }

    // Check if token is expired
    const expiryTime = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    if (expiryTime && Date.now() >= parseInt(expiryTime, 10) && localStorage.getItem(this.REFRESH_TOKEN_KEY)) {
      // Token is expired, try to refresh
      try {
        await this.refresh();
        return localStorage.getItem(this.ACCESS_TOKEN_KEY);
      } catch (error) {
        this.handleAuthError(error);
        throw error;
      }
    }

    return token;
  }

  private async refresh(): Promise<AccessTokenResponse> {
    // If refresh is already in progress, return the existing promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }
    const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    this.refreshPromise = (async () => {
      try {
        const response = await firstValueFrom(
          this.http.post<AccessTokenResponse>(`${this.baseUrl}/api/auth/refresh`, {
            refreshToken,
          }),
        );

        this.storeTokens(response);
        this.isAuthenticatedSubject.next(true);
        return response;
      } catch (error) {
        this.handleAuthError(error);
        throw error;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  private storeTokens(tokenResponse: AccessTokenResponse): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, tokenResponse.accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, tokenResponse.refreshToken);

    // Calculate expiry time (subtract 5 minutes for safety margin)
    const expiryTime = Date.now() + tokenResponse.expiresIn * 1000 - 5 * 60 * 1000;
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiryTime.toString());
  }

  private clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
  }

  private hasValidToken(): boolean {
    const token = localStorage.getItem(this.ACCESS_TOKEN_KEY);
    const expiryTime = localStorage.getItem(this.TOKEN_EXPIRY_KEY);

    if (!token || !expiryTime) {
      return false;
    }

    return Date.now() < parseInt(expiryTime, 10);
  }

  private handleAuthError(error: unknown): void {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 401) {
        this.logout();
      }
    }
    console.error('Authentication error:', error);
  }
}
